import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '../utils/api';
import { registerSessionHandlers, clearSessionHandlers } from './session';

const AuthContext = createContext(null);

const STORAGE_KEY = 'wb.auth.session';

const readStoredSession = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (!parsed.accessToken || !parsed.refreshToken || !parsed.user) {
            return null;
        }
        return parsed;
    } catch (error) {
        console.warn('Failed to parse stored auth session', error);
        return null;
    }
};

const persistSession = (session) => {
    if (!session) {
        localStorage.removeItem(STORAGE_KEY);
    } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    }
};

export function AuthProvider({ children }) {
    const [session, setSession] = useState(() => readStoredSession());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refreshTokenRef = useRef(session?.refreshToken ?? null);

    useEffect(() => {
        refreshTokenRef.current = session?.refreshToken ?? null;
    }, [session?.refreshToken]);

    const logout = useCallback(() => {
        setSession(null);
        persistSession(null);
        clearSessionHandlers();
    }, []);

    const applySession = useCallback((nextSession) => {
        setSession(nextSession);
        persistSession(nextSession);
    }, []);

    const refreshTokens = useCallback(async () => {
        const refreshToken = refreshTokenRef.current;
        if (!refreshToken) {
            throw new Error('Missing refresh token');
        }
        const result = await apiClient.refreshToken(refreshToken);
        applySession({
            accessToken: result.access_token,
            refreshToken: result.refresh_token,
            user: result.user,
        });
        return result;
    }, [applySession]);

    const login = useCallback(async (email, password) => {
        setError(null);
        const result = await apiClient.login(email, password);
        applySession({
            accessToken: result.access_token,
            refreshToken: result.refresh_token,
            user: result.user,
        });
        return result;
    }, [applySession]);

    const changePassword = useCallback(async (currentPassword, newPassword) => {
        await apiClient.changePassword({ current_password: currentPassword, new_password: newPassword });
    }, []);

    const impersonateRole = useCallback(async (role) => {
        const result = await apiClient.impersonateRole(role);
        applySession({
            accessToken: result.access_token,
            refreshToken: result.refresh_token,
            user: result.user,
        });
        return result;
    }, [applySession]);

    const revertRole = useCallback(async () => {
        const result = await apiClient.revertRole();
        applySession({
            accessToken: result.access_token,
            refreshToken: result.refresh_token,
            user: result.user,
        });
        return result;
    }, [applySession]);

    useEffect(() => {
        registerSessionHandlers({
            getAccessToken: () => session?.accessToken ?? null,
            refreshTokens,
            onUnauthorized: logout,
        });
    }, [logout, refreshTokens, session?.accessToken]);

    useEffect(() => {
        setLoading(false);
    }, []);

    const value = useMemo(() => {
        const permissions = new Set(session?.user?.permissions ?? []);
        const hasPermission = (required) => {
            if (!required) {
                return true;
            }
            if (Array.isArray(required)) {
                return required.every((perm) => permissions.has(perm));
            }
            return permissions.has(required);
        };

        return {
            user: session?.user ?? null,
            accessToken: session?.accessToken ?? null,
            refreshToken: session?.refreshToken ?? null,
            isAuthenticated: Boolean(session?.accessToken && session?.user),
            hasPermission,
            login,
            logout,
            refreshTokens,
            changePassword,
            impersonateRole,
            revertRole,
            isImpersonating: Boolean(session?.user?.impersonating),
            loading,
            error,
            setError,
        };
    }, [changePassword, error, impersonateRole, loading, login, logout, refreshTokens, revertRole, session?.accessToken, session?.refreshToken, session?.user]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return ctx;
};
